import { useSyncExternalStore } from 'react'
import { readStorage, writeStorage } from './storage'

const HIDDEN_KEY = 'agent:launcher-hidden'
const POSITION_KEY = 'agent:launcher-position'

// Offsets from the bottom-right corner, so a button left near that corner
// stays there when the window is resized.
export type LauncherPosition = { right: number; bottom: number }

const DEFAULT_POSITION: LauncherPosition = { right: 20, bottom: 20 }

// Kept outside AgentRoot, which is a lazy chunk, because the settings page
// needs it too: once the floating button is hidden, 设置 → 界面 is the way back,
// and only for people the console is open to.
// docked: the input bar is mounted and carries the AI助手 button itself, so
// the floating launcher stays away (input-center PRD §15). hidden is the
// user's own switch and applies to both.
// panelOpen: whether the console is open, so the bar's ✦ can show itself
// pressed and say 收起 (input-center PRD §15 D8).
// unread: a scheduled result nobody has opened yet, shown as a dot on the
// button, with no number (scheduled tasks design §3.3).
type LauncherState = { enabled: boolean; hidden: boolean; docked: boolean; panelOpen: boolean; unread: boolean }

let state: LauncherState = {
  enabled: false,
  hidden: readStorage(HIDDEN_KEY) === '1',
  docked: false,
  panelOpen: false,
  unread: false,
}
const listeners = new Set<() => void>()

function update(next: Partial<LauncherState>) {
  state = { ...state, ...next }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useLauncher(): LauncherState {
  return useSyncExternalStore(subscribe, () => state)
}

export function setAgentEnabled(enabled: boolean) {
  if (state.enabled !== enabled) update({ enabled })
}

export function setPanelOpen(panelOpen: boolean) {
  if (state.panelOpen !== panelOpen) update({ panelOpen })
}

export function setLauncherUnread(unread: boolean) {
  if (state.unread !== unread) update({ unread })
}

export function setLauncherDocked(docked: boolean) {
  if (state.docked !== docked) update({ docked })
}

export function setLauncherHidden(hidden: boolean) {
  writeStorage(HIDDEN_KEY, hidden ? '1' : null)
  update({ hidden })
}

export function readLauncherPosition(): LauncherPosition {
  try {
    const { right, bottom } = JSON.parse(readStorage(POSITION_KEY) ?? '')
    if (Number.isFinite(right) && Number.isFinite(bottom)) return { right, bottom }
  } catch {
    // nothing saved yet, or not ours
  }
  return DEFAULT_POSITION
}

export function writeLauncherPosition(position: LauncherPosition) {
  writeStorage(POSITION_KEY, JSON.stringify(position))
}
